import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import BottomNav from "@/components/BottomNav";
import AdvertiseModal from "@/components/AdvertiseModal";
import SuspensionScreen from "@/components/SuspensionScreen";
import ConsentMigrationModal from "@/components/ConsentMigrationModal";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Iter 44: the persistent floating "Advertise" button has been converted
 * into a **timed popup**. On the first login in a browser session the
 * AdvertiseModal auto-opens after 45 seconds — if the user dismisses it we
 * remember that for the remainder of the session (via sessionStorage). This
 * kills the button-clutter while keeping the ad-lead funnel front-and-centre.
 */
const AD_POPUP_DELAY_MS = 45000;
const AD_POPUP_KEY = "ns_advertise_popup_shown";

export default function AppLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [adOpen, setAdOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem(AD_POPUP_KEY) === "1") return;
    const t = setTimeout(() => {
      setAdOpen(true);
      sessionStorage.setItem(AD_POPUP_KEY, "1");
    }, AD_POPUP_DELAY_MS);
    return () => clearTimeout(t);
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0605] text-[#DC2626]">
        <Loader2 className="animate-spin"/>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }}/>;
  // Iter 33: gate banned/suspended users behind a dedicated appeals screen
  if (user.account_status === "banned" || user.account_status === "suspended") {
    return <SuspensionScreen user={user}/>;
  }
  // Iter 33: prompt pre-iter32 users (no age_group on their profile) for consent once
  const needsConsent = !user.age_group;
  return (
    <div className="min-h-screen bg-[#0A0605]">
      <Outlet/>
      <BottomNav/>
      <AdvertiseModal open={adOpen} onClose={() => setAdOpen(false)}/>
      {needsConsent && <ConsentMigrationModal/>}
    </div>
  );
}
